// Libraries
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { History } from 'history';

// Redux
import { addPortfoliosToComparison, setComparisonContact } from '@/redux/modules/comparison';
import { loadPortfolioForEdit, transformFDC3ToCapIntelPortfolio } from '@/redux/modules/edit-portfolio';
import { getPortfolioById } from '@/redux/modules/portfolios';
import { setCurrentContact, setMyContacts, setMyPortfolios } from '@/redux/modules/contacts';
import { setMyContextHouseholdId, setMyHouseholds } from '@/redux/modules/household';

// Strategy
import { AbstractStrategy } from './abstract-strategy';

// Types
import { PortfolioFDC3 } from '@/types/portfolio-fdc3';
import { ReduxState } from '@/redux/modules/types';
import GenericContact from '@/types/generic-contact';
import Portfolio from '@/types/portfolio';
import { FactsetContact, FactsetHousehold, FactsetLaunchContext } from '@/types/factset-household';
import { Household } from '@/types/households';
import { setUserRepcode } from '@/redux/modules/users';

// Utils
import disclaimers from '@/utils/disclaimers';
import domUtils from '@/utils/dom';
import { clonePortfolioAsProxy, loadPortfolioIfHoldingsAreMissing } from '@/utils/portfolio/utils';
import { NAV_PATHS } from '@/utils/constants';
import { formatContact, formatContactPortfolios, formatHousehold, formatHouseholdData } from '../../post-message-utils';

// Handle the case where we are launching into the comparison with a portfolio loaded from Advisor Dashboard
const handlePortfolioLaunch = async (dispatch: ThunkDispatch<ReduxState, null, AnyAction>, context: PortfolioFDC3) => {
  // Get CapIntel portfolios from FDC3 portfolios
  const portfolio = await dispatch(transformFDC3ToCapIntelPortfolio(context));

  // Save the contact of the portfolio we load as the current and comparison contact
  if (portfolio?.contact) {
    dispatch(setComparisonContact(portfolio.contact));
    dispatch(setCurrentContact(portfolio.contact));
    dispatch(setMyContacts([portfolio.contact]));
  }

  // Check if portfolio has missing holdings
  const hasMissingHoldings = disclaimers.portfolioHasMissingOrInvalidHoldings(portfolio);
  if (hasMissingHoldings) {
    // Show the Portfolio Editor with the missing holdings and go through the Missing Item flow
    const portfolioToLoad = await loadPortfolioIfHoldingsAreMissing(portfolio, (id, portfolioType, contact, enterprise, errorWhiteList, source) =>
      dispatch(getPortfolioById(id, portfolioType, contact, enterprise, errorWhiteList, source)),
    );

    // Clone the portfolio
    const clonedPortfolio = clonePortfolioAsProxy(portfolioToLoad);

    await dispatch(loadPortfolioForEdit(clonedPortfolio));

    if (domUtils.appLoaderIsVisible()) {
      domUtils.removeAppLoader();
    }
  } else {
    // Add the portfolio to comparison
    await dispatch(addPortfoliosToComparison([portfolio]));
  }
};

// Handle the case where we are launching into a contact from Advisor Dashboard
const handleContactLaunch = async (dispatch: ThunkDispatch<ReduxState, null, AnyAction>, context: FactsetContact, history: History<unknown>) => {
  const formattedPortfolios: Portfolio[] = [];
  const capintelContact: GenericContact = formatContact(context);
  formattedPortfolios.push(...formatContactPortfolios(context, capintelContact));
  dispatch(setMyContacts([capintelContact]));
  dispatch(setMyPortfolios(formattedPortfolios));

  // Navigate to contacts detail page
  history.push(NAV_PATHS.CONTACT_DETAILS.replace(':contactId', context.id.FDS_ID));
};

// Handle the case where we are launching into a household from Advisor Dashboard
const handleHouseholdLaunch = async (dispatch: ThunkDispatch<ReduxState, null, AnyAction>, context: FactsetHousehold, history: History<unknown>) => {
  const capIntelHousehold: Household = formatHousehold(context);
  const { formattedContacts, formattedPortfolios } = formatHouseholdData(context);

  dispatch(setMyContacts(formattedContacts));
  dispatch(setMyPortfolios(formattedPortfolios));
  dispatch(setMyHouseholds([capIntelHousehold], formattedPortfolios));
  dispatch(setMyContextHouseholdId(context.id));

  // Navigate to contacts detail page
  history.push(NAV_PATHS.HOUSEHOLD_DETAILS.replace(':householdId', context.id));
};

export class ContextChangedStrategy extends AbstractStrategy {
  async execute(dispatch: ThunkDispatch<ReduxState, null, AnyAction>, context: FactsetLaunchContext, history: History<unknown>): Promise<void> {
    // Update user repcode
    if (context?.repcode) {
      dispatch(setUserRepcode(context.repcode));
    }

    switch (context.type) {
      case 'fdc3.contact':
        await handleContactLaunch(dispatch, context as FactsetContact, history);
        break;
      case 'fdc3.portfolio':
        await handlePortfolioLaunch(dispatch, context as PortfolioFDC3);
        break;
      case 'fdc3.contactList':
        await handleHouseholdLaunch(dispatch, context as FactsetHousehold, history);
        break;
    }
  }
}
